/* eslint-disable no-use-before-define */
import { Popover } from 'components/Basic/Popover/Popover';
import { useForwardedRef } from 'hooks/typescript/UseForwardedRef';
import useTranslation from 'next-translate/useTranslation';
import { MinusIcon, PlusIcon } from 'public/svg/IconsSvg';
import { FormEventHandler, forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { twJoin } from 'tailwind-merge';

type SpinboxProps = {
    id: string;
    idSuffix?: string;
    min: number;
    max: number;
    step: number;
    multiple?: number;
    defaultValue: number;
    onChangeValueCallback?: (currentValue: number) => void;
    size?: 'default' | 'small';
    text?: string;
    backgroundColor?: string;
    border?: string;
    hasDynamicWidth?: boolean;
    fetching?: boolean;
    disabled?: boolean;
    overstepMessage?: {
        tooHigh?: string;
        tooLow?: string;
        onlyMultiplesOf?: string;
    };
    focusableButtons?: boolean;
    inputClassname?: string;
    className?: string;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
};

const WARNING_LABEL_DISPLAY_DURATION = 2000;

const Spinbox = forwardRef<HTMLInputElement, SpinboxProps>(
    (
        {
            backgroundColor = '#ececec',
            border = 'none',
            defaultValue,
            id,
            idSuffix = '',
            min,
            max,
            onChangeValueCallback,
            size,
            step,
            text,
            hasDynamicWidth,
            fetching,
            disabled,
            overstepMessage,
            multiple = 1,
            focusableButtons = true,
            inputClassname,
            className,
            onKeyDown,
        },
        spinboxForwardedRef,
    ) => {
        const { t } = useTranslation();
        const testIdentifier = 'forms-spinbox-';
        const [isHoldingDecrease, setIsHoldingDecrease] = useState(false);
        const [isHoldingIncrease, setIsHoldingIncrease] = useState(false);
        const [warningLabel, setWarningLabel] = useState<string | null>();
        const intervalRef = useRef<NodeJS.Timer | null>(null);
        const spinboxRef = useForwardedRef(spinboxForwardedRef);
        const spinboxMirrorRef = useRef<HTMLSpanElement | null>(null);
        const valueCorrectionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
        const previousValidValueRef = useRef(defaultValue);

        const setNewSpinboxValue = useCallback(
            (newValue: number, delayInputChange: boolean = false) => {
                let updatedNewValue = newValue;
                let warningLabel: string | null = null;

                if (Number.isNaN(newValue) || newValue < min) {
                    updatedNewValue = min;
                    warningLabel = overstepMessage?.tooLow ?? null;
                } else if (newValue > max) {
                    updatedNewValue = max;
                    warningLabel = overstepMessage?.tooHigh ?? null;
                }

                const correctionValue = updatedNewValue % multiple;
                if (correctionValue > 0) {
                    warningLabel = overstepMessage?.onlyMultiplesOf ?? null;
                    updatedNewValue -= correctionValue;
                }

                if (delayInputChange) {
                    if (valueCorrectionTimeoutRef.current) {
                        clearTimeout(valueCorrectionTimeoutRef.current);
                    }
                    valueCorrectionTimeoutRef.current = setTimeout(() => {
                        if (warningLabel) {
                            setWarningLabel(warningLabel);
                        }
                        spinboxRef.current.valueAsNumber = updatedNewValue;
                        if (spinboxMirrorRef.current !== null) {
                            spinboxMirrorRef.current.innerText = spinboxRef.current.valueAsNumber.toString();
                        }
                    }, 500);
                } else {
                    if (warningLabel) {
                        setWarningLabel(warningLabel);
                    }
                    if (spinboxRef.current !== null) {
                        spinboxRef.current.valueAsNumber = updatedNewValue;
                    }
                    if (spinboxMirrorRef.current !== null) {
                        spinboxMirrorRef.current.innerText = spinboxRef.current.valueAsNumber.toString();
                    }

                    previousValidValueRef.current = updatedNewValue;
                }

                if (onChangeValueCallback !== undefined) {
                    onChangeValueCallback(updatedNewValue);
                }
            },
            [min, max, onChangeValueCallback, spinboxRef, overstepMessage, multiple],
        );

        const onChangeValueHandler = useCallback(
            (amountChange: number) => {
                if (spinboxRef.current !== null) {
                    setNewSpinboxValue(spinboxRef.current.valueAsNumber + amountChange);
                }
            },
            [setNewSpinboxValue, spinboxRef],
        );

        useEffect(() => {
            if (isHoldingDecrease) {
                intervalRef.current = setInterval(() => {
                    onChangeValueHandler(-step);
                }, 200);
            } else {
                clearSpinboxInterval(intervalRef.current);
            }

            return () => {
                clearSpinboxInterval(intervalRef.current);
            };
        }, [isHoldingDecrease, onChangeValueHandler, step]);

        useEffect(() => {
            if (isHoldingIncrease) {
                intervalRef.current = setInterval(() => {
                    onChangeValueHandler(step);
                }, 200);
            } else {
                clearSpinboxInterval(intervalRef.current);
            }

            return () => {
                clearSpinboxInterval(intervalRef.current);
            };
        }, [isHoldingIncrease, onChangeValueHandler, step]);

        const clearSpinboxInterval = (interval: NodeJS.Timer | null) => {
            if (interval !== null) {
                clearInterval(interval);
            }
        };
        const onInputHandler: FormEventHandler<HTMLInputElement> = (event) => {
            if (spinboxRef.current !== null) {
                if (valueCorrectionTimeoutRef.current) {
                    clearTimeout(valueCorrectionTimeoutRef.current);
                    valueCorrectionTimeoutRef.current = null;
                }

                if (event.currentTarget.value === '') {
                    if (spinboxMirrorRef.current !== null) {
                        spinboxMirrorRef.current.innerText = '';
                    }

                    return;
                }

                const value = event.currentTarget.valueAsNumber;

                valueCorrectionTimeoutRef.current = setTimeout(() => {
                    setNewSpinboxValue(value, false);
                }, 500);
            }
        };

        const onBlurHandler = () => {
            if (spinboxRef.current !== null) {
                if (valueCorrectionTimeoutRef.current) {
                    clearTimeout(valueCorrectionTimeoutRef.current);
                    valueCorrectionTimeoutRef.current = null;
                }

                if (spinboxRef.current.value === '' || Number.isNaN(spinboxRef.current.valueAsNumber)) {
                    spinboxRef.current.valueAsNumber = previousValidValueRef.current;
                    if (spinboxMirrorRef.current !== null) {
                        spinboxMirrorRef.current.innerText = previousValidValueRef.current.toString();
                    }
                } else {
                    setNewSpinboxValue(spinboxRef.current.valueAsNumber, false);
                }
            }
        };

        useEffect(() => {
            spinboxRef.current.valueAsNumber = defaultValue;
            previousValidValueRef.current = defaultValue;
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [defaultValue]);

        useEffect(() => {
            const timer = setTimeout(() => {
                if (warningLabel) {
                    setWarningLabel(null);
                }
            }, WARNING_LABEL_DISPLAY_DURATION);

            return () => clearTimeout(timer);
        }, [warningLabel]);

        useEffect(() => {
            return () => {
                if (valueCorrectionTimeoutRef.current) {
                    clearTimeout(valueCorrectionTimeoutRef.current);
                }
            };
        }, []);

        const spinboxButtonTwClass = twJoin(
            'flex justify-center items-center shrink-0 p-0 web-transition text-primary cursor-pointer bg-none border-0 rounded-md outline-0 hover:bg-grayVeryLight active:scale-95',
            size === 'small' && 'translate-y-0 text-tiny',
            size === 'small' ? 'w-6 vl:w-7' : 'w-7 vl:w-9',
            disabled && 'cursor-default pointer-events-none',
        );

        return (
            <Popover label={warningLabel} variant="default">
                <div
                    className={twJoin(
                        'inline-flex items-stretch overflow-hidden rounded-md',
                        size === 'small' ? 'min-h-[30px] p-0.5' : 'min-h-[50px] p-1',
                        disabled && 'opacity-50',
                        className,
                    )}
                    style={{ backgroundColor: backgroundColor, border: border }}
                >
                    <button
                        aria-label={t('Remove one item')}
                        type="button"
                        className={spinboxButtonTwClass}
                        onClick={() => onChangeValueHandler(-step)}
                        onMouseDown={() => setIsHoldingDecrease(true)}
                        onMouseUp={() => setIsHoldingDecrease(false)}
                        onMouseLeave={() => setIsHoldingDecrease(false)}
                        data-testid={testIdentifier + 'decrease'}
                        disabled={disabled}
                        tabIndex={focusableButtons ? 0 : -1}
                    >
                        <MinusIcon className={twJoin(size === 'small' ? 'h-[14px] w-[14px]' : 'h-[18px] w-[18px]')} />
                    </button>
                    <span className="relative block overflow-hidden">
                        {hasDynamicWidth && (
                            <span
                                className="pointer-events-none invisible whitespace-pre text-h5"
                                ref={spinboxMirrorRef}
                            >
                                {defaultValue}
                            </span>
                        )}
                        <label className="sr-only" htmlFor={`addToCartAmount${id}${idSuffix}`}>
                            {t('Add to cart amount')}
                        </label>
                        <input
                            className={twJoin(
                                'm-0 h-full border-0 p-0 text-primary outline-none',
                                !hasDynamicWidth && 'w-full min-w-[40px] text-center',
                                hasDynamicWidth && 'absolute left-0 right-0 w-auto min-w-[1px] text-left',
                                fetching && 'hidden',
                                size === 'small' ? 'text-h6' : 'text-h5',
                                inputClassname,
                            )}
                            id={`addToCartAmount${id}${idSuffix}`}
                            style={{ backgroundColor: backgroundColor }}
                            ref={spinboxRef}
                            defaultValue={defaultValue}
                            onInput={onInputHandler}
                            onBlur={onBlurHandler}
                            type="number"
                            min={min}
                            max={max}
                            data-testid={testIdentifier + 'input'}
                            disabled={disabled}
                            onKeyDown={onKeyDown}
                        />
                        {fetching && (
                            <div className="flex h-10 w-10 items-center justify-center">
                                <Skeleton
                                    className="h-7 w-4 !rounded-[5px]"
                                    highlightColor="#dcdcdc"
                                    baseColor="#d3d3d3"
                                />
                            </div>
                        )}
                    </span>
                    {text !== undefined && <span className="my-auto mx-2 pt-1 text-grayVeryDark">{text}</span>}
                    <button
                        aria-label={t('Add one item')}
                        type="button"
                        className={spinboxButtonTwClass}
                        onClick={() => onChangeValueHandler(step)}
                        onMouseDown={() => setIsHoldingIncrease(true)}
                        onMouseUp={() => setIsHoldingIncrease(false)}
                        onMouseLeave={() => setIsHoldingIncrease(false)}
                        data-testid={testIdentifier + 'increase'}
                        disabled={disabled}
                        tabIndex={focusableButtons ? 0 : -1}
                    >
                        <PlusIcon className={twJoin(size === 'small' ? 'h-[14px] w-[14px]' : 'h-[18px] w-[18px]')} />
                    </button>
                </div>
            </Popover>
        );
    },
);

Spinbox.displayName = 'Spinbox';

/* @component */
export default Spinbox;
